// 🎯 BULLETPROOF DASHBOARD DATE & FILTER MANAGER
// ONE SINGLE FILE TO RULE THEM ALL - COMPLETELY RISK-FREE
// File: utils/dashboardManager.ts

import moment from 'moment';
import logger from '../../utils/logger';

// ===========================================================================================
// 🎯 TYPES & INTERFACES - Single source of truth
// ===========================================================================================

export interface DateRange {
  from: string;
  to: string;
}

export interface DashboardFilters {
  dateRange: DateRange;
  dateFilter: string; // '7d', '30d', 'custom', etc.
  isCustomDate: boolean;
  selectedUserId: string;
  selectedTeamId: string;
  isOrganization: boolean;
}

export interface DashboardState {
  filters: DashboardFilters;
  loading: {
    core: boolean;
    charts: boolean;
    tabData: boolean;
  };
  cache: {
    key: string;
    timestamp: number;
    data: any;
  };
}

// ===========================================================================================
// 🎯 DATE CONSTANTS - Single source of truth
// ===========================================================================================

export const DASHBOARD_DATE_OPTIONS = [
  { name: 'Today', value: '0d' },
  { name: 'Yesterday', value: '1d' },
  { name: 'Last 7 Days', value: '7d' },
  { name: 'Last 30 Days', value: '30d' },
  { name: 'Last 90 Days', value: '90d' },
  { name: 'This Week', value: 'this_week' },
  { name: 'This Month', value: 'this_month' },
  { name: 'Last Month', value: 'last_month' },
  { name: 'This Year', value: 'this_year' },
  { name: 'Custom', value: 'custom' }
] as const;

// ===========================================================================================
// 🎯 BULLETPROOF DATE CALCULATOR - Handles ALL date scenarios
// ===========================================================================================

export class DashboardDateManager {
  private static instance: DashboardDateManager;

  // Singleton pattern - ONE instance across entire dashboard
  public static getInstance(): DashboardDateManager {
    if (!DashboardDateManager.instance) {
      DashboardDateManager.instance = new DashboardDateManager();
    }
    return DashboardDateManager.instance;
  }

  /**
   * 🎯 MASTER DATE CALCULATOR - Handles ALL scenarios
   * This is the ONLY function that calculates dates in your entire dashboard
   */
  public calculateDateRange(filterValue: string): DateRange {
    logger.log('🎯 MASTER calculateDateRange called with:', filterValue);

    // Use local time (IST) for "today" calculations so that date boundaries match the user's timezone,
    // then convert to UTC for the API via .utc().format()
    const now = moment();
    let fromDate: moment.Moment;
    let toDate: moment.Moment = now.clone().endOf('day'); // FIXED: Default to end of day, not current second

    switch (filterValue) {
      case '0d':
      case 'today':
        fromDate = now.clone().startOf('day');
        toDate = now.clone().endOf('day');
        break;

      case '1d':
      case 'yesterday':
        fromDate = now.clone().subtract(1, 'day').startOf('day');
        toDate = now.clone().subtract(1, 'day').endOf('day');
        break;

      case '7d':
      case 'last_7_days':
        fromDate = now.clone().subtract(7, 'days').startOf('day');
        toDate = now.clone().endOf('day'); // FIXED: End of today, not current second
        break;

      case '15d':
        fromDate = now.clone().subtract(15, 'days').startOf('day');
        toDate = now.clone().endOf('day'); // FIXED
        break;

      case '30d':
      case 'last_30_days':
        fromDate = now.clone().subtract(30, 'days').startOf('day');
        toDate = now.clone().endOf('day'); // FIXED
        break;

      case '90d':
      case 'last_90_days':
        fromDate = now.clone().subtract(90, 'days').startOf('day');
        toDate = now.clone().endOf('day'); // FIXED
        break;

      case 'this_week':
      case 'thisWeek':
        fromDate = now.clone().startOf('week');
        toDate = now.clone().endOf('day'); // FIXED
        break;

      case 'last_week':
      case 'lastWeek':
        fromDate = now.clone().subtract(1, 'week').startOf('week');
        toDate = now.clone().subtract(1, 'week').endOf('week');
        break;

      case 'this_month':
      case 'thisMonth':
        fromDate = now.clone().startOf('month');
        toDate = now.clone().endOf('day'); // FIXED
        break;

      case 'last_month':
      case 'lastMonth':
        fromDate = now.clone().subtract(1, 'month').startOf('month');
        toDate = now.clone().subtract(1, 'month').endOf('month');
        break;

      case 'this_quarter':
      case 'thisQuarter':
        fromDate = now.clone().startOf('quarter');
        toDate = now.clone().endOf('day'); // FIXED
        break;

      case 'last_quarter':
      case 'lastQuarter':
        fromDate = now.clone().subtract(1, 'quarter').startOf('quarter');
        toDate = now.clone().subtract(1, 'quarter').endOf('quarter');
        break;

      case 'this_year':
      case 'thisYear':
        fromDate = now.clone().startOf('year');
        break;

      case 'last_year':
      case 'lastYear':
        fromDate = now.clone().subtract(1, 'year').startOf('year');
        toDate = now.clone().subtract(1, 'year').endOf('year');
        break;

      case 'custom':
        // For custom, return default 7d range - actual dates set by custom date inputs
        fromDate = now.clone().subtract(7, 'days').startOf('day');
        break;

      default:
        // Handle numeric values or fallback
        const numericValue = parseInt(filterValue);
        if (!isNaN(numericValue) && numericValue >= 0) {
          fromDate = now.clone().subtract(numericValue, 'days').startOf('day');
        } else {
          console.warn('⚠️ Unknown date filter, defaulting to 7 days:', filterValue);
          fromDate = now.clone().subtract(7, 'days').startOf('day');
        }
        break;
    }

    const result = {
      from: fromDate.utc().format(),
      to: toDate.utc().format()
    };

    logger.log('✅ MASTER Date range calculated:', {
      filter: filterValue,
      from: result.from,
      to: result.to,
      fromFormatted: fromDate.format('YYYY-MM-DD HH:mm:ss'),
      toFormatted: toDate.format('YYYY-MM-DD HH:mm:ss'),
      daysDiff: moment(result.to).diff(moment(result.from), 'days')
    });

    return result;
  }

  /**
   * 🎯 BULLETPROOF API DATE PARAMS - Used by ALL dashboard API calls
   * This is the ONLY function that provides dates to APIs
   */
  public getAPIDateParams(filters: DashboardFilters): { fromDate: string; toDate: string } {
    logger.log('🎯 MASTER getAPIDateParams called with filters:', filters);

    const result = {
      fromDate: filters.dateRange.from,
      toDate: filters.dateRange.to
    };

    logger.log('✅ API date params:', {
      fromDate: result.fromDate,
      toDate: result.toDate,
      fromFormatted: moment(result.fromDate).format('YYYY-MM-DD HH:mm:ss'),
      toFormatted: moment(result.toDate).format('YYYY-MM-DD HH:mm:ss')
    });

    return result;
  }

  /**
   * 🎯 CUSTOM DATE HANDLER - Bulletproof custom date processing
   */
  public createCustomDateRange(fromDate: string, toDate: string): DateRange {
    logger.log('🎯 MASTER createCustomDateRange called:', { fromDate, toDate });

    // Parse as UTC dates directly (don't convert timezone)
    const fromMoment = moment.utc(fromDate).startOf('day');
    const toMoment = moment.utc(toDate).endOf('day');

    const result = {
      from: fromMoment.format(),
      to: toMoment.format()
    };

    logger.log('✅ Custom date range created:', {
      from: result.from,
      to: result.to,
      fromFormatted: fromMoment.format('YYYY-MM-DD HH:mm:ss'),
      toFormatted: toMoment.format('YYYY-MM-DD HH:mm:ss'),
      daysDiff: toMoment.diff(fromMoment, 'days')
    });

    return result;
  }
}

// ===========================================================================================
// 🎯 BULLETPROOF FILTER MANAGER - Handles ALL filter combinations
// ===========================================================================================

class DashboardFilterManager {
  private static instance: DashboardFilterManager;
  private dateManager: DashboardDateManager;
  
  constructor() {
    this.dateManager = DashboardDateManager.getInstance();
  }

  public static getInstance(): DashboardFilterManager {
    if (!DashboardFilterManager.instance) {
      DashboardFilterManager.instance = new DashboardFilterManager();
    }
    return DashboardFilterManager.instance;
  }

  /**
   * 🎯 MASTER FILTER STATE CREATOR - Creates consistent filter state
   */
  public createFilters(
    dateFilter: string = '7d',
    customFromDate?: string,
    customToDate?: string,
    selectedUserId: string = '',
    selectedTeamId: string = '',
    isOrganization: boolean = false
  ): DashboardFilters {
    logger.log('🎯 MASTER createFilters called:', {
      dateFilter, customFromDate, customToDate, selectedUserId, selectedTeamId, isOrganization
    });

    const isCustom = dateFilter === 'custom' && Boolean(customFromDate) && Boolean(customToDate);
    
    let dateRange: DateRange;
    if (isCustom) {
      dateRange = this.dateManager.createCustomDateRange(customFromDate!, customToDate!);
    } else {
      dateRange = this.dateManager.calculateDateRange(dateFilter);
    }

    const filters: DashboardFilters = {
      dateRange,
      dateFilter,
      isCustomDate: isCustom,
      selectedUserId,
      selectedTeamId,
      isOrganization: Boolean(isOrganization)
    };

    logger.log('✅ MASTER filters created:', filters);
    return filters;
  }

  /**
   * 🎯 CACHE KEY GENERATOR - Consistent cache keys across dashboard
   */
  public generateCacheKey(filters: DashboardFilters, additionalParams: Record<string, any> = {}): string {
    const baseKey = [
      filters.dateRange.from,
      filters.dateRange.to,
      filters.selectedUserId,
      filters.selectedTeamId,
      filters.isOrganization.toString(),
      ...Object.values(additionalParams)
    ].join('|');

    logger.log('🎯 Cache key generated:', baseKey);
    return baseKey;
  }

  /**
   * 🎯 FILTER COMPARISON - Check if filters have changed
   */
  public filtersHaveChanged(oldFilters: DashboardFilters, newFilters: DashboardFilters): boolean {
    const oldKey = this.generateCacheKey(oldFilters);
    const newKey = this.generateCacheKey(newFilters);
    const changed = oldKey !== newKey;
    
    logger.log('🎯 Filters comparison:', { changed, oldKey, newKey });
    return changed;
  }
}

// ===========================================================================================
// 🎯 DASHBOARD STATE MANAGER - Manages entire dashboard state
// ===========================================================================================

class DashboardStateManager {
  private static instance: DashboardStateManager;
  private filterManager: DashboardFilterManager;
  private dateManager: DashboardDateManager;
  private currentState: DashboardState;
  
  constructor() {
    this.filterManager = DashboardFilterManager.getInstance();
    this.dateManager = DashboardDateManager.getInstance();
    this.currentState = this.createInitialState();
  }

  public static getInstance(): DashboardStateManager {
    if (!DashboardStateManager.instance) {
      DashboardStateManager.instance = new DashboardStateManager();
    }
    return DashboardStateManager.instance;
  }

  private createInitialState(): DashboardState {
    return {
      filters: this.filterManager.createFilters('7d'),
      loading: {
        core: false,
        charts: false,
        tabData: false
      },
      cache: {
        key: '',
        timestamp: 0,
        data: null
      }
    };
  }

  /**
   * 🎯 UPDATE FILTERS - Single method to update all filters
   */
  public updateFilters(updates: Partial<DashboardFilters>): DashboardFilters {
    logger.log('🎯 MASTER updateFilters called:', updates);

    const newFilters = { ...this.currentState.filters, ...updates };
    
    // If date filter changed, recalculate date range
    if (updates.dateFilter && !updates.isCustomDate) {
      newFilters.dateRange = this.dateManager.calculateDateRange(updates.dateFilter);
      newFilters.isCustomDate = false;
    }
    
    // If custom date, update date range
    if (updates.isCustomDate && updates.dateRange) {
      newFilters.dateRange = updates.dateRange;
      newFilters.dateFilter = 'custom';
    }

    this.currentState.filters = newFilters;
    
    logger.log('✅ MASTER filters updated:', newFilters);
    return newFilters;
  }

  /**
   * 🎯 GET CURRENT FILTERS - Always returns current filter state
   */
  public getCurrentFilters(): DashboardFilters {
    return { ...this.currentState.filters };
  }

  /**
   * 🎯 GET API PARAMS - Universal API parameter provider
   */
  public getAPIParams(additionalParams: Record<string, any> = {}): Record<string, any> {
    const dateParams = this.dateManager.getAPIDateParams(this.currentState.filters);
    
    const apiParams = {
      ...dateParams,
      ...(this.currentState.filters.selectedUserId && { userId: this.currentState.filters.selectedUserId }),
      ...(this.currentState.filters.selectedTeamId && { teamId: this.currentState.filters.selectedTeamId }),
      ...(this.currentState.filters.isOrganization && { isOrganization: true }),
      ...additionalParams
    };

    logger.log('🎯 MASTER API params generated:', apiParams);
    return apiParams;
  }

  /**
   * 🎯 CACHE MANAGEMENT
   */
  public shouldRefreshData(newFilters: DashboardFilters): boolean {
    const cacheAge = Date.now() - this.currentState.cache.timestamp;
    const filtersChanged = this.filterManager.filtersHaveChanged(this.currentState.filters, newFilters);
    const cacheExpired = cacheAge > 30000; // 30 seconds
    
    const shouldRefresh = filtersChanged || cacheExpired;
    logger.log('🎯 Cache check:', { shouldRefresh, filtersChanged, cacheExpired, cacheAge });
    
    return shouldRefresh;
  }

  public updateCache(data: any, filters: DashboardFilters): void {
    this.currentState.cache = {
      key: this.filterManager.generateCacheKey(filters),
      timestamp: Date.now(),
      data
    };
    logger.log('🎯 Cache updated');
  }
}

// ===========================================================================================
// 🎯 PUBLIC API - Simple, bulletproof interface for your dashboard
// ===========================================================================================

const dashboardManager = DashboardStateManager.getInstance();

/**
 * 🎯 MAIN API - Use these functions in your dashboard
 */

// Get current filters
export const getCurrentFilters = (): DashboardFilters => {
  return dashboardManager.getCurrentFilters();
};

// Update date filter (handles both predefined and custom)
export const updateDateFilter = (
  dateFilter: string,
  customFromDate?: string,
  customToDate?: string
): DashboardFilters => {
  logger.log('🎯 API updateDateFilter called:', { dateFilter, customFromDate, customToDate });
  
  if (dateFilter === 'custom' && customFromDate && customToDate) {
    const dateManager = DashboardDateManager.getInstance();
    const dateRange = dateManager.createCustomDateRange(customFromDate, customToDate);
    return dashboardManager.updateFilters({
      dateFilter: 'custom',
      dateRange,
      isCustomDate: true
    });
  } else {
    return dashboardManager.updateFilters({
      dateFilter,
      isCustomDate: false
    });
  }
};

// Update user/team filters
export const updateUserFilter = (userId: string): DashboardFilters => {
  logger.log('🎯 API updateUserFilter called:', userId);
  return dashboardManager.updateFilters({ selectedUserId: userId });
};

export const updateTeamFilter = (teamId: string): DashboardFilters => {
  logger.log('🎯 API updateTeamFilter called:', teamId);
  return dashboardManager.updateFilters({ selectedTeamId: teamId });
};

export const updateOrganizationFilter = (isOrganization: boolean): DashboardFilters => {
  logger.log('🎯 API updateOrganizationFilter called:', isOrganization);
  return dashboardManager.updateFilters({ isOrganization });
};

// Get API parameters for any dashboard API call
export const getAPIParams = (additionalParams: Record<string, any> = {}): Record<string, any> => {
  return dashboardManager.getAPIParams(additionalParams);
};

// Check if data should be refreshed
export const shouldRefreshData = (): boolean => {
  const current = dashboardManager.getCurrentFilters();
  return dashboardManager.shouldRefreshData(current);
};

// Update cache
export const updateDataCache = (data: any): void => {
  const current = dashboardManager.getCurrentFilters();
  dashboardManager.updateCache(data, current);
};

// Debug helper
export const debugDashboardState = (): void => {
 
};

// ===========================================================================================
// 🎯 USAGE EXAMPLES FOR YOUR DASHBOARD
// ===========================================================================================

/*
// In your dashboard component:

// 1. Update date filter
const handleDateFilterChange = (filterValue: string) => {
  const newFilters = updateDateFilter(filterValue);
  // Trigger data refresh
  loadDashboardData();
};

// 2. Update custom date
const handleCustomDateChange = (fromDate: string, toDate: string) => {
  const newFilters = updateDateFilter('custom', fromDate, toDate);
  // Trigger data refresh
  loadDashboardData();
};

// 3. Update user filter
const handleUserChange = (userId: string) => {
  const newFilters = updateUserFilter(userId);
  // Trigger data refresh
  loadDashboardData();
};

// 4. In ANY API function (replace ALL your existing date logic):
const DashboardLeadCount = async () => {
  try {
    setLoading(true);
    
    // 🎯 THIS IS ALL YOU NEED - ONE LINE
    const apiParams = getAPIParams();
    
    const response = await fetchDashboardLeadCount(
      apiParams.fromDate,
      apiParams.toDate,
      apiParams.isOrganization,
      apiParams.userId,
      apiParams.teamId
    );
    
    // Handle response...
  } catch (error) {
    console.error('API Error:', error);
  } finally {
    setLoading(false);
  }
};

// 5. For APIs with additional parameters:
const DashboardGraph = async (tabName: string) => {
  const apiParams = getAPIParams({ tab: tabName });
  // Use apiParams...
};
*/